import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {filter, take, takeUntil} from 'rxjs/operators';
import {MemoizedSelector, select, Store} from '@ngrx/store';

import {UtilsService} from '@core/services/utils.service';
import {ConnectionsList} from '@core/services/connections-list.service';
import {MenuAsideService} from '@core/services/layout/menu-aside.service';
import {selectLineageConnections} from '@store/lineage';
import {selectDiscoveryConnections} from '@store/discovery';
import {selectCompareETLConnections} from '@core/states/compare-etl.state';
import {selectCompareREPORTConnections} from '@core/states/compare-report.state';
import {LineageService} from '@main/lineage-dashboard/lineage.service';
import {DiscoveryService} from '@main/discovery/discovery.service';
import {ModuleUtilsService} from '@core/services/module-utils.service';
import {Locations} from '@shared/enums/locations.enum';
import {getLocation, IConnection, selectConnectionsDisabled} from '@store/common';
import {SetLineageConnections} from '@store/lineage/actions';
import {SetDiscoveryConnections} from '@store/discovery/actions';
import {SetCompareEtlConnections} from '@core/actions/compare-etl.actions';
import {SetCompareReportConnections} from '@core/actions/compare-report.actions';
import {Subject} from 'rxjs';
import {ButtonsTheme} from '@shared/enums/searchType';
import {E2EColumnDashboardService} from '@main/e2e-column-dashboard/e2e-column-dashboard.service';

@Component({
    selector: 'oct-list-settings',
    templateUrl: './list-settings.component.html',
    styleUrls: ['./list-settings.component.scss'],
})
export class ListSettingsComponent implements OnInit, AfterViewInit, OnDestroy {
    public isCollapsed = true;
    public needToDisabled: boolean;
    public selectedModuleIndex: number;
    public connectionsCollections: { connections: any[], connectionsBG: any[] };
    public connections: any[];
    public allExpandState: boolean = false;
    private location: string;
    private componentDestroy$ = new Subject();

    constructor(
        private store: Store<any>,
        private utilsService: UtilsService,
        public connectionsList: ConnectionsList,
        private menuAsideService: MenuAsideService,
        private lineageService: LineageService,
        private discoveryService: DiscoveryService,
        public moduleUtilsService: ModuleUtilsService,
        private e2eColumnDashboardService: E2EColumnDashboardService,
    ) {
    }

    ngOnInit(): void {
        this.store
            .pipe(
                takeUntil(this.componentDestroy$),
                select(getLocation),
            )
            .subscribe((location: Locations) => {
                this.location = this.moduleUtilsService.getLocationPreferredKey(location);
                this.registerToConnectionsChanges();
            });

        this.store
            .pipe(
                select(selectConnectionsDisabled),
                takeUntil(this.componentDestroy$),
            )
            .subscribe(value => {
                this.needToDisabled = value;
            });
    }

    ngAfterViewInit(): void {
        this.discoveryService.toggleGridSubject
            .subscribe(data => {
                this.needToDisabled = data;
            });
    }

    getConnectionsSelectorByLocation(): MemoizedSelector<any, any> {
        switch (this.location) {
            case Locations.lineage:
            case Locations.e2eColumn:
            case Locations.e2eColumnDashboard:
            case Locations.lineageSchema:
            case Locations.map:
                return selectLineageConnections;
            case Locations.discovery:
                return selectDiscoveryConnections;
            case Locations.compareEtls:
                return selectCompareETLConnections;
            case Locations.compareReports:
                return selectCompareREPORTConnections;
            default:
                return null;
        }
    }

    public expandCollapseState() {
        this.selectedModuleIndex = null;
        this.isCollapsed ? this.collapseState() : this.expandState();
        this.isCollapsed = !this.isCollapsed;
    }

    expandState() {
        this.allExpandState = false;
    }

    collapseState() {
        this.allExpandState = true;
    }

    public onCheckAllClick(isChecked: boolean): void {
        if (this.needToDisabled) {
            return;
        }

        for (const key of Object.keys(this.connectionsList.moduleCounter)) {
            this.connectionsList.moduleCounter[key].checked = isChecked ? 0 : this.connectionsList.moduleCounter[key].total;
        }

        // inner function
        const setConnectionStatus = connections => {
            connections.forEach((a: { key: string, value: any[] }) => {
                a.value.forEach((b: { key: string, value: any[] }) => {
                    b.value.forEach((connection: IConnection) => {
                        if (connection.isBG === false) {
                            isChecked ? this.connectionsList.moduleCounter[connection.module_type].checked++ : this.connectionsList.moduleCounter[connection.module_type].checked--;
                        }
                        connection.is_checked = isChecked;
                        this.connectionsList.updateStorage(connection);
                    });
                });
            });
        };

        const connectionsObj = {
            connections: this.connectionsCollections.connections,
            connectionsBG: this.connectionsCollections.connectionsBG
        };
        setConnectionStatus(connectionsObj.connections);
        setConnectionStatus(connectionsObj.connectionsBG);
        switch (this.location) {
            case Locations.e2eColumnDashboard:
            case Locations.lineage:
            case Locations.map:
            case Locations.lineageSchema:
                this.store.dispatch(new SetLineageConnections(connectionsObj));
                break;
            case Locations.discovery:
                this.store.dispatch(new SetDiscoveryConnections(connectionsObj));
                break;
            case Locations.compareEtls:
                this.store.dispatch(new SetCompareEtlConnections(connectionsObj));
                break;
            case Locations.compareReports:
                this.store.dispatch(new SetCompareReportConnections(connectionsObj));
                break;
        }

        this.connectionsList.updateConnectionsIDsList(connectionsObj);
        this.connectionsList.updateStore();
        this.menuAsideService.updateBadgeValue();

        // Update legend tools when check/uncheck all is clicked
        if (this.location === Locations.lineage ||
            this.location === Locations.map ||
            this.location === Locations.lineageSchema ||
            this.location === Locations.e2eColumnDashboard) {
            this.updateAllLegendTools();
        }
    }

    public setOpenSelectedMenuStyle(i) {
        return i === this.selectedModuleIndex;
    }

    public toggleSelectedModule(panelOpenState, i) {
        if (panelOpenState) { // open
            this.selectedModuleIndex = i;
        } else { // close
            this.selectedModuleIndex = null;
        }
    }

    isChecked(connection: IConnection): void {
        connection.is_checked = !connection.is_checked;
        if (connection.isBG === false) {
            connection.is_checked ?
                this.connectionsList.moduleCounter[connection.module_type].checked++ :
                this.connectionsList.moduleCounter[connection.module_type].checked--;
        }

        this.connectionsList.updateConnectionsIDsList(this.connectionsCollections);
        this.connectionsList.updateStorage(connection);
        this.connectionsList.updateStore(connection);
        this.menuAsideService.updateBadgeValue();

        const connections = {
            connections: this.connectionsCollections.connections,
            connectionsBG: this.connectionsCollections.connectionsBG
        };
        switch (this.location) {
            case Locations.e2eColumnDashboard:
            case Locations.lineage:
            case Locations.map:
            case Locations.lineageSchema:
                this.store.dispatch(new SetLineageConnections(connections));
                // Update legend tools based on connection check status
                this.updateLegendToolsForConnection(connection);
                break;
            case Locations.discovery:
                this.store.dispatch(new SetDiscoveryConnections(connections));
                break;
            case Locations.compareEtls:
                this.store.dispatch(new SetCompareEtlConnections(connections));
                break;
            case Locations.compareReports:
                this.store.dispatch(new SetCompareReportConnections(connections));
                break;
        }
    }

    private updateAllLegendTools(): void {
        // Determine which service to use based on location
        const isE2ELocation = this.location === Locations.e2eColumn ||
            this.location === Locations.e2eColumnDashboard;

        if (isE2ELocation) {
            // Update E2E legend tools based on checked connections
            this.e2eColumnDashboardService.legendTools.forEach(legendItem => {
                const hasCheckedConnection = this.isAnyConnectionCheckedForTool(
                    legendItem.type,
                    legendItem.toolName
                );

                // Set strike to true (shown with strikethrough) if no connections are checked
                legendItem.strike = !hasCheckedConnection;

                // Remove from selectedLegendItems if no connections checked
                if (!hasCheckedConnection) {
                    this.e2eColumnDashboardService.selectedLegendItems[legendItem.type] =
                        this.e2eColumnDashboardService.selectedLegendItems[legendItem.type]?.filter(
                            item => item.toolName !== legendItem.toolName
                        ) || [];
                }
            });

            this.e2eColumnDashboardService.legendToolsUpdatedSubject.next();
        } else {
            // Update lineage legend tools based on checked connections
            this.lineageService.legendTools.forEach(legendItem => {
                const hasCheckedConnection = this.isAnyConnectionCheckedForTool(
                    legendItem.type,
                    legendItem.toolName
                );

                // Set active flag based on connection state (true if checked, false if unchecked)
                legendItem.active = hasCheckedConnection;

                // Remove from selectedLegendItems if no connections checked
                if (!hasCheckedConnection) {
                    this.lineageService.selectedLegendItems[legendItem.type] =
                        this.lineageService.selectedLegendItems[legendItem.type]?.filter(
                            item => item.toolName !== legendItem.toolName
                        ) || [];
                }
            });

            this.lineageService.legendToolsUpdatedSubject.next();
        }
    }

    private updateLegendToolsForConnection(connection: IConnection): void {
        // Check if any other connection with the same module_type and tool is still checked
        const hasOtherCheckedConnection = this.isAnyConnectionCheckedForTool(
            connection.module_type,
            connection.tool
        );

        // Determine which service to use based on location
        const isE2ELocation = this.location === Locations.e2eColumn ||
            this.location === Locations.e2eColumnDashboard;

        if (isE2ELocation) {
            // Update E2E legend tools for this specific connection's tool
            const legendItems = this.e2eColumnDashboardService.getLegendItems(connection.module_type);
            legendItems.forEach(legendItem => {
                if (legendItem.toolName === connection.tool) {
                    // Set strike to true if no connections are checked
                    legendItem.strike = !hasOtherCheckedConnection;

                    // Remove from selectedLegendItems if no connections checked
                    if (!hasOtherCheckedConnection) {
                        this.e2eColumnDashboardService.selectedLegendItems[legendItem.type] =
                            this.e2eColumnDashboardService.selectedLegendItems[legendItem.type]?.filter(
                                item => item.toolName !== legendItem.toolName
                            ) || [];
                    }
                }
            });
            this.e2eColumnDashboardService.legendToolsUpdatedSubject.next();
        } else {
            // Update lineage legend tools for this specific connection's tool
            const legendItems = this.lineageService.getLegendItems(connection.module_type);
            legendItems.forEach(legendItem => {
                if (legendItem.toolName === connection.tool) {
                    // Set active flag based on connection state (true if checked, false if unchecked)
                    legendItem.active = hasOtherCheckedConnection;

                    // Remove from selectedLegendItems if no connections checked
                    if (!hasOtherCheckedConnection) {
                        this.lineageService.selectedLegendItems[legendItem.type] =
                            this.lineageService.selectedLegendItems[legendItem.type]?.filter(
                                item => item.toolName !== legendItem.toolName
                            ) || [];
                    }
                }
            });
            this.lineageService.legendToolsUpdatedSubject.next();
        }
    }

    private isAnyConnectionCheckedForTool(moduleType: string, toolName: string): boolean {
        // Check in both connections and connectionsBG arrays
        const checkInArray = (connectionsArray: any[]): boolean => {
            if (!connectionsArray) {
                return false;
            }

            for (const moduleGroup of connectionsArray) {
                if (!moduleGroup || !moduleGroup.value) {
                    continue;
                }

                for (const toolGroup of moduleGroup.value) {
                    if (!toolGroup || !toolGroup.value) {
                        continue;
                    }

                    for (const conn of toolGroup.value) {
                        if (conn.module_type === moduleType &&
                            conn.tool === toolName &&
                            conn.is_checked) {
                            return true;
                        }
                    }
                }
            }
            return false;
        };

        return checkInArray(this.connectionsCollections.connections) ||
            checkInArray(this.connectionsCollections.connectionsBG);
    }

    ngOnDestroy(): void {
        this.componentDestroy$.next(undefined);
        this.componentDestroy$.unsubscribe();
    }

    private registerToConnectionsChanges() {
        const selector = this.getConnectionsSelectorByLocation();
        if (selector) {
            this.store
                .pipe(
                    select(selector),
                    filter((connectionsCollections: any) => connectionsCollections && !!connectionsCollections.connections),
                    take(1),
                )
                .subscribe(results => {
                    this.connectionsCollections = results;
                });
        }
    }

    public get buttonThemeColor(): typeof ButtonsTheme {
        return ButtonsTheme;
    }
}
